import { useEffect } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { Loader } from 'src/components/ui'
import { useGetUserQuery } from 'src/redux/api'
import { isSignedOutError } from 'src/helpers/authState'
import ServerUnavailable from 'src/components/elements/server-unavailable/ServerUnavailable'
import { socket } from 'src/socket'

const unauthorizedPaths = [
  '/login',
  '/registration',
  '/login/create-request-without-auth',
  '/forgot-password',
  '/reset-password',
]
const completeRegistrationPath = '/complete-registration'

// A password-reset link from email must open even when the browser already
// holds a live session (possibly another account) — otherwise the user is
// bounced to the cabinet instead of the new-password form.
const alwaysAccessiblePaths = ['/reset-password']

// Sections that genuinely need an account. Everything else (the map/search
// storefront, unknown URLs → 404) renders for guests too, so a visitor can
// browse listings before being asked to log in. Guarded by prefix because
// /messenger/:id style children must match as well.
const authRequiredPrefixes = [
  '/cabinet',
  '/notifications',
  '/profile',
  '/wishlist',
  '/create-ad',
  '/agreement',
  '/support',
  '/messenger',
]

export const AuthGuard = () => {
  // TODO: when not complete auth with google and return back i see profile
  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useGetUserQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  })
  const location = useLocation()
  const navigate = useNavigate()
  const currentPath = location.pathname

  // Where to land after a successful login: the page the guest was bounced
  // from (login-wall), defaulting to home.
  const afterLoginPath = (location.state as { from?: string } | null)?.from || '/'

  useEffect(() => {
    // Redirect fully registered users away from unauthorized paths
    if (
      user &&
      !user.notRegistered &&
      unauthorizedPaths.includes(currentPath) &&
      !alwaysAccessiblePaths.includes(currentPath)
    ) {
      navigate(afterLoginPath)
    }

    // Redirect partially registered users away from complete registration if they're already on it
    if (
      user &&
      user.notRegistered &&
      currentPath !== completeRegistrationPath &&
      !unauthorizedPaths.includes(currentPath)
    ) {
      navigate(completeRegistrationPath, { state: { from: currentPath } })
    }
  }, [user, currentPath, navigate, afterLoginPath])

  useEffect(() => {
    // Only connect socket for fully registered users
    if (user && !user.notRegistered) {
      socket.connect()
      return () => {
        socket.disconnect()
      }
    } else {
      socket.disconnect()
    }
  }, [user])

  if (isLoading) return <Loader />

  // Case 1: No user. Auth pages and guest-browsable routes render; the home
  // page for a guest is the search storefront; account-only sections bounce to
  // login carrying the intended destination so login can return there.
  if (!user) {
    if (unauthorizedPaths.includes(currentPath)) return <Outlet />
    if (authRequiredPrefixes.some((prefix) => currentPath.startsWith(prefix))) {
      // The account could not be loaded because the server is down, not
      // because nobody is signed in: offer a retry instead of the login page.
      if (error && !isSignedOutError(error)) return <ServerUnavailable onRetry={refetch} />
      return <Navigate to="/login" state={{ from: currentPath + location.search }} />
    }
    return <Outlet />
  }

  // Case 2: Partially registered user - allow access to unauthorized paths and complete registration
  if (user.notRegistered) {
    return unauthorizedPaths.includes(currentPath) || currentPath === completeRegistrationPath ? (
      <Outlet />
    ) : (
      <Navigate to={completeRegistrationPath} state={{ from: currentPath }} />
    )
  }

  // Case 3: Fully registered user - allow access to all paths except unauthorized ones
  return !unauthorizedPaths.includes(currentPath) || alwaysAccessiblePaths.includes(currentPath) ? (
    <Outlet />
  ) : (
    <Navigate to={afterLoginPath} />
  )
}
